import { ApiError, ApiAuthError, ApiResponseError } from './ApiError.js';
import { ErrorCode } from '../../core/errors/ErrorCodes.js';

// The single place an HTTP status becomes a classified SDK error.
//
// Centralizing this is the whole point of the requirement that no UI component
// calls fetch() directly: without it, every call site would invent its own
// idea of what a 429 or a 503 means.
//
// Recoverability is a property of the failure, not a retry decision. RetryPolicy
// reads it; this function never retries anything itself (ADR-026: an error
// describes a failure, it never performs recovery).
export function errorForStatus(status, { endpoint, body = null } = {}) {
  const context = { endpoint, status, ...extractServerMessage(body) };

  if (status === 401 || status === 403) {
    return new ApiAuthError(`API authentication failed (HTTP ${status})`, {
      status,
      endpoint,
      // Recoverable: AuthManager can obtain a fresh token and the caller can
      // legitimately try again once.
      recoverable: true,
      code: status === 401 ? ErrorCode.SDK_API_TOKEN_EXPIRED : ErrorCode.SDK_API_UNAUTHORIZED,
      context,
    });
  }

  if (status >= 500) {
    return new ApiError(`API server error (HTTP ${status})`, {
      status,
      endpoint,
      // A 5xx is transient by definition - the request was well-formed.
      recoverable: true,
      code: ErrorCode.SDK_API_SERVER_ERROR,
      context,
    });
  }

  if (status === 408 || status === 429) {
    return new ApiError(`API request throttled or timed out (HTTP ${status})`, {
      status,
      endpoint,
      recoverable: true,
      code: ErrorCode.SDK_API_SERVER_ERROR,
      context,
    });
  }

  if (status >= 400) {
    return new ApiError(`API rejected the request (HTTP ${status})`, {
      status,
      endpoint,
      // A 4xx means the request itself was wrong. Retrying it unchanged would
      // fail identically, so it is deliberately NOT recoverable.
      recoverable: false,
      code: ErrorCode.SDK_API_BAD_REQUEST,
      context,
    });
  }

  return new ApiResponseError(`API returned an unexpected status (HTTP ${status})`, {
    status,
    endpoint,
    recoverable: false,
    context,
  });
}

// A server-supplied message is useful in diagnostics, but the body is
// untrusted and unbounded. Only a short string is carried through, and
// sanitizeContext() truncates it again downstream.
function extractServerMessage(body) {
  if (!body || typeof body !== 'object') return {};
  const message = body.message ?? body.error ?? null;
  return typeof message === 'string' ? { serverMessage: message.slice(0, 200) } : {};
}
